import React, { createContext, useEffect, useState } from 'react';
import { FlexCrate, Text } from '@perch-labs/shrike';
import { Switch, Route, Redirect } from 'react-router-dom';
import { copyFor, routes } from 'config';
import { CenterGridColumn } from 'styles';
import { BackLink } from 'components/back-link';
import { NavLinkTab } from 'components/navigation/nav-link-tab';
import { useAccountPlans } from 'hooks/use-account-plans.hook';
import { Content, StyledGrid, SubNav, BackgroundDiv } from './membership.style';
import { PlanSettingsContext } from './membership.typings';
import { PaginatedPlans } from './components/paginated-plans';

const getCopy = copyFor('settings.membership');
const { settings, inactivePlans, activePlans } = routes();

interface CenterProps {
  mobileFullWidth?: boolean;
  children: React.ReactNode;
}

const Centered = ({ children, mobileFullWidth = false }: CenterProps) => (
  <StyledGrid mobileFullWidth={mobileFullWidth}>
    <CenterGridColumn>{children}</CenterGridColumn>
  </StyledGrid>
);

export const MembershipContext = createContext<PlanSettingsContext | null>(
  null
);

export const Membership = () => {
  const [refreshPlans, setRefreshPlans] = useState(false);
  const [activePageCount, setActivePageCount] = useState(0);
  const [inActivePageCount, setInActivePageCount] = useState(0);
  const [membershipContextValue, setMembershipContextValue] = useState({
    refreshPlans,
    setRefreshPlans,
  });

  const totalActivePlans = useAccountPlans(activePageCount, 'active');
  const totalInActivePlans = useAccountPlans(inActivePageCount, 'inactive');

  useEffect(() => {
    totalActivePlans?.refetch();
    totalInActivePlans?.refetch();
    setMembershipContextValue({ refreshPlans, setRefreshPlans });
  }, [refreshPlans]);

  return (
    <>
      <BackgroundDiv>
        <BackLink to={settings} />
        <Centered>
          <Text textStyle="heading800" tag="h1">
            {getCopy('title')}
          </Text>
        </Centered>
        <SubNav>
          <Centered>
            <FlexCrate height="64px">
              <NavLinkTab
                copy={getCopy('tabs.active')}
                path={activePlans}
                padding="24px 0px"
                margin={{ right: '36px' }}
              />
              <NavLinkTab
                copy={getCopy('tabs.inactive')}
                path={inactivePlans}
                padding="24px 0px"
              />
            </FlexCrate>
          </Centered>
        </SubNav>
      </BackgroundDiv>
      <Content>
        <MembershipContext.Provider value={membershipContextValue}>
          <Centered mobileFullWidth={true}>
            <Switch>
              <Route
                path={activePlans}
                render={props => (
                  <PaginatedPlans
                    {...props}
                    type="active"
                    planData={totalActivePlans}
                    page={activePageCount}
                    setPage={setActivePageCount}
                    refreshPlans={refreshPlans}
                  />
                )}
              />
              <Route
                path={inactivePlans}
                render={props => (
                  <PaginatedPlans
                    {...props}
                    type="inactive"
                    planData={totalInActivePlans}
                    page={inActivePageCount}
                    setPage={setInActivePageCount}
                    refreshPlans={refreshPlans}
                  />
                )}
              />
              <Redirect to={activePlans} />
            </Switch>
          </Centered>
        </MembershipContext.Provider>
      </Content>
    </>
  );
};
